import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Calendar, Globe, Lock, Globe2, Copy, Check, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useLocale } from "@/contexts/LocaleContext";
import { supabase } from "@/integrations/supabase/client";
import { useRateLimit, RATE_LIMIT_CONFIGS } from "@/hooks/useRateLimit";

const EVENT_CREATION_COST = 50;
const FIRST_EVENT_REWARD = 100;
const COIN_TO_USD_RATE = 0.05; // $0.05 per coin

interface CreateEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEventCreated: () => void;
}

const CreateEventModal = ({ isOpen, onClose, onEventCreated }: CreateEventModalProps) => {
  const { user, profile, refetchProfile } = useAuth();
  const { locale, t } = useLocale();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isFirstEvent, setIsFirstEvent] = useState<boolean | null>(null);
  const { isLimited, checkRateLimit, recordAction } = useRateLimit(RATE_LIMIT_CONFIGS.eventCreation);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "other" as const,
    language: "global" as const,
    deadline: "",
    isPrivate: false,
  });

  // Check if this is user's first event
  useEffect(() => {
    const checkFirstEvent = async () => {
      if (!user) return;
      const { count } = await supabase
        .from("events")
        .select("*", { count: "exact", head: true })
        .eq("creator_id", user.id);
      setIsFirstEvent(count === 0);
    };
    checkFirstEvent();
  }, [user]);

  const userCoins = profile?.duv_coins ?? 0;
  const hasEnoughCoins = isFirstEvent || userCoins >= EVENT_CREATION_COST;

  const categories = [
    { value: "sports", label: t("events.sports") },
    { value: "crypto", label: t("events.crypto") },
    { value: "politics", label: t("events.politics") },
    { value: "tech", label: t("events.tech") },
    { value: "entertainment", label: t("events.entertainment") },
    { value: "other", label: locale.language === "pt" ? "Outro" : locale.language === "es" ? "Otro" : locale.language === "fr" ? "Autre" : locale.language === "de" ? "Sonstige" : locale.language === "it" ? "Altro" : "Other" },
  ];

  const eventLanguages = [
    { value: "global", flag: "🌍", label: { en: "Global", pt: "Global", es: "Global", fr: "Global", de: "Global", it: "Globale" } },
    { value: "en", flag: "🇺🇸", label: { en: "English", pt: "Inglês", es: "Inglés", fr: "Anglais", de: "Englisch", it: "Inglese" } },
    { value: "pt", flag: "🇧🇷", label: { en: "Portuguese", pt: "Português", es: "Portugués", fr: "Portugais", de: "Portugiesisch", it: "Portoghese" } },
    { value: "es", flag: "🇪🇸", label: { en: "Spanish", pt: "Espanhol", es: "Español", fr: "Espagnol", de: "Spanisch", it: "Spagnolo" } },
    { value: "fr", flag: "🇫🇷", label: { en: "French", pt: "Francês", es: "Francés", fr: "Français", de: "Französisch", it: "Francese" } },
    { value: "de", flag: "🇩🇪", label: { en: "German", pt: "Alemão", es: "Alemán", fr: "Allemand", de: "Deutsch", it: "Tedesco" } },
    { value: "it", flag: "🇮🇹", label: { en: "Italian", pt: "Italiano", es: "Italiano", fr: "Italien", de: "Italienisch", it: "Italiano" } },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    // Rate limit check
    if (!checkRateLimit()) {
      toast({
        title: locale.language === "pt" ? "Muitos eventos" : "Too many events",
        description: locale.language === "pt" 
          ? "Aguarde um pouco antes de criar outro evento" 
          : "Please wait before creating another event",
        variant: "destructive",
      });
      return;
    }

    // Check if user has enough coins (skip for first event)
    if (!isFirstEvent && !hasEnoughCoins) {
      toast({
        title: locale.language === "pt" ? "Saldo insuficiente" : "Insufficient balance",
        description: locale.language === "pt" 
          ? `Você precisa de ${EVENT_CREATION_COST} DC para criar um evento. Saldo atual: ${userCoins} DC`
          : `You need ${EVENT_CREATION_COST} DC to create an event. Current balance: ${userCoins} DC`,
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // For first event, reward coins. Otherwise, deduct coins.
      if (isFirstEvent) {
        // Reward user with coins for first event
        const { error: rewardError } = await supabase.rpc('refund_user_balance', {
          _user_id: user.id,
          _amount: FIRST_EVENT_REWARD
        });

        if (rewardError) {
          console.error('Failed to reward first event bonus:', rewardError);
        }
      } else {
        // Atomically deduct coins from user's balance using RPC
        const { error: deductError } = await supabase.rpc('deduct_event_cost', {
          _user_id: user.id,
          _cost: EVENT_CREATION_COST
        });

        if (deductError) {
          // Handle insufficient balance error gracefully
          if (deductError.message.includes('Insufficient balance')) {
            toast({
              title: locale.language === "pt" ? "Saldo insuficiente" : "Insufficient balance",
              description: locale.language === "pt" 
                ? "Você não tem moedas suficientes para criar este evento"
                : "You don't have enough coins to create this event",
              variant: "destructive",
            });
            return;
          }
          throw deductError;
        }
      }

      const { data: eventData, error } = await supabase.from("events").insert({
        creator_id: user.id,
        title: formData.title,
        description: formData.description,
        category: formData.category,
        country: formData.language as any,
        deadline: formData.deadline,
        is_private: formData.isPrivate,
      } as any).select().single();

      if (error) throw error;

      // Record the action for rate limiting
      recordAction();

      // Record revenue from event creation fee (only for non-first events)
      if (!isFirstEvent) {
        const revenueAmount = EVENT_CREATION_COST * COIN_TO_USD_RATE; // $2.50
        await supabase.from("platform_revenue").insert({
          amount: revenueAmount,
          fee_type: "event_creation",
          event_id: eventData.id,
        });
      }

      // Refetch profile to update coin balance in UI
      refetchProfile?.();

      // If private event, create an invite link
      if (formData.isPrivate && eventData) {
        const { data: inviteData, error: inviteError } = await supabase
          .from("event_invites")
          .insert({ event_id: eventData.id })
          .select()
          .single();

        if (inviteError) throw inviteError;

        const link = `${window.location.origin}/invite/${inviteData.invite_token}`;
        setInviteLink(link);
        
        toast({
          title: locale.language === "pt" ? "Evento privado criado!" : "Private event created!",
          description: isFirstEvent
            ? (locale.language === "pt" 
                ? `Compartilhe o link de convite. +${FIRST_EVENT_REWARD} DC de bônus!` 
                : `Share the invite link with participants. +${FIRST_EVENT_REWARD} DC bonus!`)
            : (locale.language === "pt" 
                ? `Compartilhe o link de convite. ${EVENT_CREATION_COST} DC deduzidos.` 
                : `Share the invite link with participants. ${EVENT_CREATION_COST} DC deducted.`),
        });
      } else {
        toast({
          title: locale.language === "pt" ? "Evento criado!" : "Event created!",
          description: isFirstEvent
            ? (locale.language === "pt" 
                ? `Seu evento foi publicado. +${FIRST_EVENT_REWARD} DC de bônus!`
                : `Your event has been published. +${FIRST_EVENT_REWARD} DC bonus!`)
            : (locale.language === "pt" 
                ? `Seu evento foi publicado. ${EVENT_CREATION_COST} DC deduzidos.`
                : `Your event has been published. ${EVENT_CREATION_COST} DC deducted.`),
        });
        
        resetAndClose();
      }

      onEventCreated();
    } catch (error: any) {
      toast({
        title: locale.language === "pt" ? "Erro" : "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetAndClose = () => {
    setFormData({
      title: "",
      description: "",
      category: "other",
      language: "global",
      deadline: "",
      isPrivate: false,
    });
    setInviteLink(null);
    setCopied(false);
    onClose();
  };

  const copyInviteLink = async () => {
    if (inviteLink) {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40"
          />
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={resetAndClose}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="glass-card p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-foreground">
                  {locale.language === "pt" ? "Criar Novo Evento" : "Create New Event"}
                </h2>
                <button
                  onClick={resetAndClose}
                  className="p-2 rounded-lg hover:bg-muted transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">
                    {locale.language === "pt" ? "Título" : "Title"}
                  </label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder={locale.language === "pt" ? "Bitcoin vai atingir $150K?" : "Will Bitcoin hit $150K?"}
                    required
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">
                    {locale.language === "pt" ? "Descrição" : "Description"}
                  </label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={locale.language === "pt" ? "Descrição detalhada da previsão..." : "Detailed description of the prediction..."}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-1 block">
                      {locale.language === "pt" ? "Categoria" : "Category"}
                    </label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value as any })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground mb-1 block">
                      <Globe size={14} className="inline mr-1" />
                      {locale.language === "pt" ? "Idioma" : locale.language === "es" ? "Idioma" : locale.language === "fr" ? "Langue" : locale.language === "de" ? "Sprache" : locale.language === "it" ? "Lingua" : "Language"}
                    </label>
                    <Select
                      value={formData.language}
                      onValueChange={(value) => setFormData({ ...formData, language: value as any })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {eventLanguages.map((lang) => (
                          <SelectItem key={lang.value} value={lang.value}>
                            {lang.flag} {lang.label[locale.language] || lang.label.en}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground mb-1 block">
                    <Calendar size={14} className="inline mr-1" />
                    {locale.language === "pt" ? "Prazo" : "Deadline"}
                  </label>
                  <Input
                    type="datetime-local"
                    value={formData.deadline}
                    onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                    required
                  />
                </div>

                {/* Privacy Toggle */}
                <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-muted/30">
                  <div className="flex items-center gap-3">
                    {formData.isPrivate ? (
                      <Lock size={18} className="text-primary" />
                    ) : (
                      <Globe2 size={18} className="text-muted-foreground" />
                    )}
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {formData.isPrivate 
                          ? (locale.language === "pt" ? "Evento Privado" : "Private Event")
                          : (locale.language === "pt" ? "Evento Público" : "Public Event")
                        }
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formData.isPrivate 
                          ? (locale.language === "pt" 
                              ? "Apenas convidados podem participar" 
                              : "Only invited users can participate")
                          : (locale.language === "pt" 
                              ? "Visível para todos na plataforma" 
                              : "Visible to everyone on the platform")
                        }
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.isPrivate}
                    onCheckedChange={(checked) => setFormData({ ...formData, isPrivate: checked })}
                  />
                </div>

                {/* Invite Link Display (after private event creation) */}
                {inviteLink && (
                  <div className="p-4 rounded-lg border border-primary/30 bg-primary/5">
                    <p className="text-sm font-medium text-foreground mb-2">
                      {locale.language === "pt" ? "Link de convite:" : "Invite link:"}
                    </p>
                    <div className="flex gap-2">
                      <Input 
                        value={inviteLink} 
                        readOnly 
                        className="text-xs bg-background"
                      />
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="icon"
                        onClick={copyInviteLink}
                      >
                        {copied ? <Check size={16} /> : <Copy size={16} />}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {locale.language === "pt" 
                        ? "Compartilhe este link para convidar participantes" 
                        : "Share this link to invite participants"}
                    </p>
                  </div>
                )}

                {/* Cost/Reward Display */}
                <div className={`flex items-center justify-between p-3 rounded-lg border ${isFirstEvent ? 'border-green-500/30 bg-green-500/5' : 'border-primary/30 bg-primary/5'}`}>
                  <div className="flex items-center gap-2">
                    <Coins size={18} className={isFirstEvent ? "text-green-500" : "text-primary"} />
                    <span className="text-sm font-medium text-foreground">
                      {isFirstEvent 
                        ? (locale.language === "pt" ? "Bônus do primeiro evento:" : "First event bonus:")
                        : (locale.language === "pt" ? "Custo de criação:" : "Creation cost:")}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className={`font-bold ${isFirstEvent ? "text-green-500" : "text-primary"}`}>
                      {isFirstEvent ? `+${FIRST_EVENT_REWARD}` : EVENT_CREATION_COST} DC
                    </span>
                    <p className="text-xs text-muted-foreground">
                      {locale.language === "pt" ? "Seu saldo:" : "Your balance:"} {userCoins.toLocaleString()} DC
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  {inviteLink ? (
                    <Button type="button" variant="hero" onClick={resetAndClose} className="flex-1">
                      {locale.language === "pt" ? "Concluído" : "Done"}
                    </Button>
                  ) : (
                    <>
                      <Button type="button" variant="ghost" onClick={resetAndClose} className="flex-1">
                        {locale.language === "pt" ? "Cancelar" : "Cancel"}
                      </Button>
                      <Button 
                        type="submit" 
                        variant="hero" 
                        className="flex-1" 
                        disabled={isLoading || !hasEnoughCoins}
                      >
                        {isLoading 
                          ? (locale.language === "pt" ? "Criando..." : "Creating...") 
                          : (locale.language === "pt" ? `Criar (${EVENT_CREATION_COST} DC)` : `Create (${EVENT_CREATION_COST} DC)`)
                        }
                      </Button>
                    </>
                  )}
                </div>
              </form>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CreateEventModal;
